import type { Options } from "canvas-confetti";

/**
 * Celebration confetti, loaded on demand.
 *
 * The library is only pulled in when a burst actually fires, so the pricing
 * page and the paywall keep their current bundle — most visitors never
 * trigger one. Every failure mode is swallowed: confetti is decoration, it
 * must never take a checkout flow down with it.
 */
export async function fireConfetti(options: Options = {}) {
	if (typeof window === "undefined") {
		return;
	}

	// Honour the OS-level motion preference. A full-screen particle burst is
	// exactly the kind of thing that setting exists for.
	if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
		return;
	}

	try {
		const { default: confetti } = await import("canvas-confetti");
		confetti({
			particleCount: 80,
			spread: 70,
			origin: { y: 0.4 },
			disableForReducedMotion: true,
			...options,
		});
	} catch {
		// Chunk failed to load (offline, blocked CDN) — nothing to do.
	}
}

/**
 * Fires at most once per browser tab session for the given key. Used to keep
 * repeat views of the same page from re-celebrating, which reads as spam.
 * A blocked sessionStorage just means the burst fires every time, which is
 * the safe direction to fail.
 */
export async function fireConfettiOncePerSession(
	key: string,
	options: Options = {},
) {
	try {
		if (sessionStorage.getItem(key)) {
			return;
		}
		sessionStorage.setItem(key, "1");
	} catch {
		// sessionStorage unavailable — fall through and fire anyway.
	}

	await fireConfetti(options);
}
